import { worstFreshness, type Freshness, type FreshnessKey, type FreshnessSource, type Me } from '@ft/shared';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getLang, useI18n, type T } from '../i18n';
import { api } from '../lib/http';
import { dtfmt } from '../lib/format';

/** Datenstand der Abgleiche und Importe: Kopfzeile (immer sichtbar) und Hinweis auf der betroffenen Seite. */

const TONE = { ok: 'pos', old: 'crit', stale: 'neg', none: 'neu' } as const;

function monthName(ym: string | null): string {
  if (!ym) return '–';
  return new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1)).toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Kurzer Zustandstext einer Quelle, z. B. „seit 11 Arbeitstagen nicht gelaufen“ oder „Oktober 2026 fehlt“. */
export function freshnessText(s: FreshnessSource, t: T): string {
  if (s.state === 'none') return t('fresh.never');
  if (s.missingMonth) return t('fresh.monthMissing', { m: monthName(s.missingMonth) });
  if (s.state === 'stale') return t('fresh.notRunning', { n: s.ageDays ?? 0 });
  if (s.state === 'old') return t('fresh.age', { n: s.ageDays ?? 0 });
  return t('fresh.current');
}

function Row({ s, t }: { s: FreshnessSource; t: T }) {
  return (
    <div className="frow">
      <i className={`fdot ${TONE[s.state]}`} />
      <div>
        <b>{t(`fresh.src.${s.key}`)}</b>{s.lastMonth ? ` · ${t('fresh.until', { m: monthName(s.lastMonth) })}` : ''}
        <div className="muted small">{s.at ? t('fresh.last', { at: dtfmt(s.at) }) : t('fresh.never')} · {t(`fresh.exp.${s.key}`)}</div>
      </div>
      <span className={`pill ${TONE[s.state]}`}>{freshnessText(s, t)}</span>
    </div>
  );
}

function useFreshnessData(enabled: boolean, dep: string): Freshness | null {
  const [data, setData] = useState<Freshness | null>(null);
  useEffect(() => { if (enabled) api<Freshness>('GET', '/api/freshness').then(setData, () => undefined); }, [enabled, dep]);
  return data;
}

/** Kopfzeile: Punkt in der Farbe des schlechtesten Stands; Klick zeigt alle Quellen. Nicht für Leser. */
export function FreshnessChip({ me }: { me: Me }) {
  const { t } = useI18n();
  const loc = useLocation();
  const data = useFreshnessData(me.role !== 'reader' && me.areas.length > 0, loc.pathname);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  if (!data) return null;
  const known = data.sources.filter((s) => s.state !== 'none');
  const worst = known.length ? worstFreshness(known) : 'none';
  const bad = known.filter((s) => s.state === 'old' || s.state === 'stale').length;
  const label = worst === 'none' ? t('fresh.title') : worst === 'ok' ? t('fresh.chip.ok') : t(worst === 'stale' ? 'fresh.chip.stale' : 'fresh.chip.old', { n: bad });
  return (
    <div className="fresh" ref={ref}>
      <button className="freshbtn" aria-expanded={open} onClick={() => setOpen((o) => !o)} title={t('fresh.title')}>
        <i className={`fdot ${TONE[worst]}`} /><span className="fl">{label}</span>
      </button>
      {open && (
        <div className="fpanel" role="dialog" aria-label={t('fresh.title')}>
          <div className="fhead">{t('fresh.title')}</div>
          {data.sources.map((s) => <Row key={s.key} s={s} t={t} />)}
          <p className="muted small" style={{ margin: '8px 0 0' }}>{t('fresh.help')}</p>
        </div>
      )}
    </div>
  );
}

/** Hinweis oben auf einer Seite, wenn eine ihrer Quellen alt oder veraltet ist. */
export function StaleBanner({ me, keys }: { me: Me; keys: FreshnessKey[] }) {
  const { t } = useI18n();
  const loc = useLocation();
  const data = useFreshnessData(me.role !== 'reader' && me.areas.length > 0, loc.pathname);
  const list = (data?.sources ?? []).filter((s) => keys.includes(s.key) && (s.state === 'old' || s.state === 'stale'));
  if (!list.length) return null;
  return (
    <>
      {list.map((s) => (
        <div key={s.key} className={`banner ${s.state === 'stale' ? 'neg' : 'crit'}`} role="status">
          <i className={`fdot ${TONE[s.state]}`} />
          <div>
            <b>{t(`fresh.banner.${s.key}`, { m: monthName(s.missingMonth), n: s.ageDays ?? 0 })}</b>
            <div className="small muted">{s.at ? t('fresh.last', { at: dtfmt(s.at) }) : ''} · {t(`fresh.exp.${s.key}`)}</div>
          </div>
        </div>
      ))}
    </>
  );
}
